import { Image } from '@babylonjs/gui';
import { Asset, AssetType } from '../Asset';
import { AssetFactory } from '../AssetFactory';

interface SubTexture {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export class AtlasLoader {
  private textures: Map<string, SubTexture> = new Map();
  private imageUrl: string;

  constructor(
    private assetFactory: AssetFactory,
    private xmlName: string,
    private imageName: string,
    private invertY: boolean = false,
    private atlasWidth: number = 2048,
    private atlasHeight: number = 2048
  ) {
    this.imageUrl = 'assets/atlas/' + imageName;
  }

  async load() {
    // Load XML
    const xmlAsset = new Asset(this.xmlName, AssetType.TEXT, 'assets/atlas/', this.xmlName + '.xml');

    // We assume the image is loaded by the browser when setting the src on the control,
    // or we could preload it here. For now, simple URL reference is enough for GUI Image.

    try {
      if (!this.assetFactory.isLoaded(this.xmlName)) {
        await this.assetFactory.load([xmlAsset]);
      }

      const asset = this.assetFactory.getAsset(this.xmlName);
      const task = asset.task as any; // Cast to access 'text' property of TextFileAssetTask

      if (task && task.text) {
        this.parseXml(task.text);
      }
    } catch (e) {
      console.error('Failed to load atlas XML', this.xmlName, e);
    }
  }

  private parseXml(xmlText: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');
    const subTextures = doc.getElementsByTagName('SubTexture');
    for (let i = 0; i < subTextures.length; i++) {
      const node = subTextures[i];
      const name = node.getAttribute('name')!;
      const x = parseInt(node.getAttribute('x')!);
      const y = parseInt(node.getAttribute('y')!);
      const width = parseInt(node.getAttribute('width')!);
      const height = parseInt(node.getAttribute('height')!);
      this.textures.set(name, { name, x, y, width, height });
    }
  }

  createImage(name: string, width: string | number = '64px', height: string | number = '64px'): Image | null {
    const data = this.textures.get(name);
    if (!data) {
      // console.warn(`Sprite ${name} not found in atlas ${this.xmlName}`);
      return null;
    }

    const img = new Image(name, this.imageUrl);
    img.width = width;
    img.height = height;

    // Babylon GUI Image cropping
    img.sourceLeft = data.x;

    if (this.invertY) {
      img.sourceTop = this.atlasHeight - data.y - data.height;
    } else {
      img.sourceTop = data.y;
    }

    img.sourceWidth = data.width;
    img.sourceHeight = data.height;

    return img;
  }
}
